import { useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import { FormattedMessage } from 'react-intl';
import { fetchPartners, fetchPartnersUsersByPartnerId } from '@/lib/api';
import {
  ContactSchema,
  PartnerSchema,
  SharedBasketRecipientMode,
} from '@/lib/types';
import useInfiniteScroll from '@/hooks/useInfiniteScroll';
import RadioButton from '@/components/RadioButton/RadioButton';
import Select from '@/components/Select/Select';
import Checkbox from '@/components/Checkbox/Checkbox';
import Button from '@/components/Button/Button';

export type SelectedPartner = Pick<PartnerSchema, 'id' | 'name'>;

const RecipientPicker = ({
  mode,
  onModeChange,
  partner,
  onPartnerChange,
  contactUserIds,
  onContactUserIdsChange,
  disabled,
}: {
  mode: SharedBasketRecipientMode;
  onModeChange: (mode: SharedBasketRecipientMode) => void;
  partner: SelectedPartner | null;
  onPartnerChange: (partner: SelectedPartner | null) => void;
  contactUserIds: number[];
  onContactUserIdsChange: (ids: number[]) => void;
  disabled?: boolean;
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isPopperReady, setIsPopperReady] = useState(false);
  const [partnerSearchText, setPartnerSearchText] = useState('');

  const handlePopperRef = (el: HTMLDivElement | null) => {
    scrollContainerRef.current = el;
    setIsPopperReady(!!el);
  };

  // /api/partners pages at 10 by default, so a single unparameterised fetch
  // only ever saw the oldest partners - page through all of them instead and
  // search server-side.
  const {
    data: partners,
    isLoading: partnersLoading,
    isFetchingNextPage,
  } = useInfiniteScroll<PartnerSchema>({
    fetchFn: fetchPartners,
    queryKey: ['share-basket-partners', partnerSearchText] as const,
    enabled: !disabled && isPopperReady,
    params: partnerSearchText ? { search: partnerSearchText } : {},
    scrollContainerRef:
      scrollContainerRef as React.RefObject<HTMLElement>,
    pageSize: 20,
    scrollThreshold: 200,
  });

  const { data: contacts, isLoading: contactsLoading } = useQuery({
    queryKey: ['partnersUsers', partner?.id],
    queryFn: () => fetchPartnersUsersByPartnerId(partner!.id),
    enabled: mode === 'contacts' && partner != null,
  });

  const contactList: ContactSchema[] = contacts ?? [];
  const allSelected =
    contactList.length > 0 &&
    contactList.every((contact) => contactUserIds.includes(contact.id));

  const partnerOptions = partners.map((item) => ({
    id: item.id,
    value: item.id,
    label: item.name,
    text: item.name,
  }));

  // The selected partner may not be in the currently loaded/searched page.
  const selectedPartnerOptions = useMemo(() => {
    if (!partner) return undefined;
    return [{ id: partner.id, value: partner.id, label: partner.name }];
  }, [partner]);

  const partnerSelect = (
    <Select
      ref={handlePopperRef}
      options={partnerOptions}
      selectedOptions={selectedPartnerOptions}
      value={partner?.id ?? null}
      onChange={(value) => {
        if (value == null) {
          onPartnerChange(null);
        } else {
          const id = Number(value);
          const picked = partners.find((item) => item.id === id);
          onPartnerChange({ id, name: picked?.name ?? String(id) });
        }
        // The contact list is scoped to one partner, so ids picked under the
        // previous one are no longer offered and must not linger.
        onContactUserIdsChange([]);
      }}
      placeholder={
        <FormattedMessage
          id="selectPartner"
          defaultMessage="Select a partner"
        />
      }
      searchEnabled
      onSearchChange={setPartnerSearchText}
      loading={partnersLoading}
      loadingNextPage={isFetchingNextPage}
      disabled={disabled}
      aria-label="Partner"
    />
  );

  return (
    <>
      <FieldLabel>
        <FormattedMessage id="shareWith" defaultMessage="Share with" />
      </FieldLabel>

      <RadioCard $selected={mode === 'partner'}>
        <RadioButton
          checked={mode === 'partner'}
          disabled={disabled}
          onChange={() => onModeChange('partner')}
          value="partner"
          name="shared-basket-recipient-mode"
          inputProps={{ 'aria-label': 'A partner' }}
        />
        <CardBody>
          <CardTitle>
            <FormattedMessage
              id="recipientModePartner"
              defaultMessage="A partner"
            />
          </CardTitle>
          <CardText>
            <FormattedMessage
              id="recipientModePartnerHint"
              defaultMessage="Any contact belonging to this partner can open the link. Best when the files may be forwarded internally."
            />
          </CardText>
          {mode === 'partner' && (
            <SelectWrapper>{partnerSelect}</SelectWrapper>
          )}
        </CardBody>
      </RadioCard>

      <RadioCard $selected={mode === 'contacts'}>
        <RadioButton
          checked={mode === 'contacts'}
          disabled={disabled}
          onChange={() => onModeChange('contacts')}
          value="contacts"
          name="shared-basket-recipient-mode"
          inputProps={{ 'aria-label': 'Specific contacts' }}
        />
        <CardBody>
          <CardTitle>
            <FormattedMessage
              id="recipientModeContacts"
              defaultMessage="Specific contacts"
            />
          </CardTitle>
          <CardText>
            <FormattedMessage
              id="recipientModeContactsHint"
              defaultMessage="Only the people you pick can open the link. Select as many as you need — one link is shared with all of them."
            />
          </CardText>
        </CardBody>
      </RadioCard>

      {mode === 'contacts' && (
        <>
          {/* Filters the contact list only - never part of the payload. */}
          <SelectWrapper>{partnerSelect}</SelectWrapper>
          {partner != null && (
            <>
              <ContactList>
                {contactsLoading ? (
                  <EmptyRow>
                    <FormattedMessage
                      id="loadingContacts"
                      defaultMessage="Loading contacts…"
                    />
                  </EmptyRow>
                ) : contactList.length === 0 ? (
                  <EmptyRow>
                    <FormattedMessage
                      id="noContactsForPartner"
                      defaultMessage="This partner has no contacts to share with."
                    />
                  </EmptyRow>
                ) : (
                  contactList.map((contact) => (
                    <ContactRow key={contact.id}>
                      <Checkbox
                        checked={contactUserIds.includes(contact.id)}
                        disabled={disabled}
                        onChange={(checked) =>
                          onContactUserIdsChange(
                            checked
                              ? [...contactUserIds, contact.id]
                              : contactUserIds.filter(
                                  (id) => id !== contact.id,
                                ),
                          )
                        }
                        aria-label={`Select ${contact.name}`}
                      />
                      <div>
                        <ContactName>{contact.name}</ContactName>
                        <ContactEmail>{contact.email}</ContactEmail>
                      </div>
                    </ContactRow>
                  ))
                )}
              </ContactList>
              {contactList.length > 0 && (
                <ContactTools>
                  <span>
                    <FormattedMessage
                      id="contactsSelectedCount"
                      defaultMessage="{selected} of {total} selected"
                      values={{
                        selected: contactUserIds.length,
                        total: contactList.length,
                      }}
                    />
                  </span>
                  <Button
                    variant="text"
                    customVariant="text-small"
                    disabled={disabled}
                    onClick={() =>
                      onContactUserIdsChange(
                        allSelected
                          ? []
                          : contactList.map((contact) => contact.id),
                      )
                    }
                    aria-label={
                      allSelected
                        ? 'Clear all contacts'
                        : 'Select all contacts'
                    }
                  >
                    {allSelected ? (
                      <FormattedMessage
                        id="clearAll"
                        defaultMessage="Clear all"
                      />
                    ) : (
                      <FormattedMessage
                        id="selectAll"
                        defaultMessage="Select all"
                      />
                    )}
                  </Button>
                </ContactTools>
              )}
            </>
          )}
        </>
      )}
    </>
  );
};

const FieldLabel = styled('div')(({ theme }) => ({
  fontSize: 13,
  fontWeight: 600,
  color: theme.palette.grey600,
  marginBottom: theme.spacing(1),
}));

const RadioCard = styled('div', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: 'flex',
  // Not `stretch`: a card-height radio box makes MUI's hover circle an
  // ellipse.
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  borderRadius: theme.radii.sm,
  border: `1px solid ${
    $selected ? theme.palette.primary.main : theme.palette.grey300
  }`,
  background: $selected ? theme.palette.red50 : theme.palette.white,
}));

const CardBody = styled('div')({ flexGrow: 1 });

const CardTitle = styled('div')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.grey900,
  marginTop: theme.spacing(1),
}));

const CardText = styled('div')(({ theme }) => ({
  fontSize: 13,
  lineHeight: '18px',
  color: theme.palette.grey500,
  marginTop: 2,
}));

const SelectWrapper = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1.5),
}));

const ContactList = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  maxHeight: 200,
  overflowY: 'auto',
  border: `1px solid ${theme.palette.grey200}`,
  borderRadius: theme.radii.sm,
}));

const ContactRow = styled('label')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 1.5),
  cursor: 'pointer',
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.grey100}`,
  },
}));

const EmptyRow = styled('div')(({ theme }) => ({
  padding: theme.spacing(2),
  fontSize: 13,
  color: theme.palette.grey500,
}));

const ContactName = styled('div')(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.grey900,
}));

const ContactEmail = styled('div')(({ theme }) => ({
  fontSize: 12,
  color: theme.palette.grey500,
}));

const ContactTools = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(0.5),
  fontSize: 13,
  color: theme.palette.grey500,
}));

export default RecipientPicker;
